/* eslint-disable no-plusplus,react/jsx-pascal-case */
import PropTypes from 'prop-types';
import React from 'react';
import { getExperimentID } from 'ctm-app/client/store/selectors';
import { pageContext } from 'ctm-util-functions';
import { useSelector } from 'react-redux';
import { SimpleFlexbox } from '../../../flexbox/flexbox';
import { experimentIDEnum } from '../../../../../ctm-vertical-base/base/reducers/page';
import styles from './layout_results_control.css';

/**
 * Main layout component for rendering a unavailable results row
 * @param {Object} props - The component's props
 * @param {Array} props.xs - componentLists for xs breakpoint and above
 * @param {Array} props.sm - componentLists for sm breakpoint and above
 * @param {Array} props.md - componentLists for md breakpoint and above
 * @param {Array} props.xl - componentLists for xl breakpoint and above
 * @param {Object} props.extraProps - The stateful product object passed in
 * @returns {Node} - The row layout component
 */
const LayoutResultsControl = ({ xs, sm, md, xl, ...extraProps }) => {
    const breakpoint = pageContext.getProps().pageState.breakpoint;
    const experimentID = useSelector(getExperimentID);

    switch (breakpoint) {
        case 'xs':
            return <_Xs componentLists={xs} {...extraProps} />;
        case 'sm':
            return <_Sm componentLists={sm} {...extraProps} />;
        case 'md':
        case 'lg':
            return <_Md componentLists={md} experimentID={experimentID} {...extraProps} />;
        default: {
            // XL to XXL breakpoint
            return <_Md componentLists={xl} experimentID={experimentID} {...extraProps} />;
        }
    }
};

LayoutResultsControl.propTypes = {
    xs: PropTypes.arrayOf(PropTypes.array),
    sm: PropTypes.arrayOf(PropTypes.array),
    md: PropTypes.arrayOf(PropTypes.array),
    xl: PropTypes.arrayOf(PropTypes.array)
};

/**
 * Private component to render layout for XS breakpoint
 * @param {Object} props - The components props
 * @param {Array} props.componentLists - all sub component lists to be rendered under this layout
 * @param {Object} props.extraProps - The stateful product object passed in
 * @returns {Node} Xs layout component
 * @private
 */
const _Xs = ({ componentLists, ...extraProps }) => {
    let i = 0;
    const { isFeaturesMode, ...componentProps } = extraProps;
    return (
        <SimpleFlexbox container>
            <SimpleFlexbox className={styles.row} wrap contentCenter="xs">
                <_ColumnLeft componentList={componentLists[i++]} />
                {isFeaturesMode ? <_ColumnMiddle componentList={componentLists[i++]} componentProps={componentProps} /> : null}
            </SimpleFlexbox>
        </SimpleFlexbox>
    );
};

_Xs.propTypes = {
    componentLists: PropTypes.arrayOf(PropTypes.array)
};

/**
 * Private component to render layout for SM breakpoint
 * @param {Object} props - The components props
 * @param {Array} props.componentLists - all sub component lists to be rendered under this layout
 * @param {Object} props.extraProps - The stateful product object passed in
 * @returns {Node} Sm layout component
 * @private
 */
const _Sm = ({ componentLists, ...extraProps }) => {
    let i = 0;
    const { isFeaturesMode, ...componentProps } = extraProps;
    return (
        <SimpleFlexbox className={styles.row} contentBetween="sm">
            <_ColumnLeft componentList={componentLists[i++]} />
            {isFeaturesMode ? <_ColumnMiddle componentList={componentLists[i++]} componentProps={componentProps} /> : null}
            <_ColumnRight componentList={componentLists[i]} />
        </SimpleFlexbox>
    );
};

_Sm.propTypes = {
    componentLists: PropTypes.arrayOf(PropTypes.array)
};

/**
 * Private component to render layout for MD breakpoint and up
 * @param {Object} props - The components props
 * @param {Array} props.componentLists - all sub component lists to be rendered under this layout
 * @param {Object} props.experimentID - The experiment ID from state
 * @param {Object} props.extraProps - The stateful product object passed in
 * @returns {Node} Md layout component
 * @private
 */
const _Md = ({ componentLists, experimentID, ...extraProps }) => {
    let i = 0;
    const { pageState: { breakpoint } } = pageContext.getProps();
    const { isFeaturesMode, ...componentProps } = extraProps;
    const belowXl = !['xl', 'xxl'].includes(breakpoint);

    // For CTM_REBRAND experiment, use xl sizing (10) instead of xxl sizing (12)
    let xlSize = breakpoint === 'xl' ? 10 : 12;
    if (experimentID === experimentIDEnum.CTM_REBRAND && breakpoint === 'xxl') {
        xlSize = 10;
    }

    return (
        <SimpleFlexbox container>
            <SimpleFlexbox
                className={styles.row}
                md={9}
                xl={xlSize}
                contentStart={!isFeaturesMode ? 'md' : false}
                contentBetween={isFeaturesMode ? 'md' : false}
                contentCenter={isFeaturesMode ? 'xl' : false}
            >
                {!isFeaturesMode ||  belowXl ? <_ColumnLeft componentList={componentLists[i++]} /> : null}
                {isFeaturesMode ? <_ColumnMiddle componentList={componentLists[i++]} componentProps={componentProps} /> : null}
            </SimpleFlexbox>
        </SimpleFlexbox>
    );
};

_Md.propTypes = {
    componentLists: PropTypes.arrayOf(PropTypes.array)
};

/**
 * Private component to render layout for the left column in the results control
 * @param {Object} props - The components props
 * @param {Array} props.componentList - components to be rendered in this layout
 * @returns {_ColumnLeft} Left column layout
 * @private
 */
const _ColumnLeft = ({ componentList }) => {
    const { constructComponents } = pageContext.getProps().director;
    return (
        <SimpleFlexbox xs={8} sm={4} md={6} lg={5} contentCenter="xs" itemsCenter="xs" gapless="xs">
            {constructComponents(componentList)}
        </SimpleFlexbox>
    );
};

_ColumnLeft.propTypes = {
    componentList: PropTypes.array
};

/**
 * Private component to render layout for middle column in the results control
 * @param {Object} props - The components props
 * @param {Array} props.componentList - components to be rendered in this layout
 * @param {Object} props.componentProps - The stateful product object passed in
 * @returns {_ColumnMiddle} Middle column layout
 * @private
 */
const _ColumnMiddle = ({ componentList, componentProps }) => {
    const { constructComponents } = pageContext.getProps().director;
    return (
        <SimpleFlexbox xs={12} sm={4} display="flex" contentCenter="xs" gapless="sm" xlOffset={3}>
            {constructComponents(componentList, { ...componentProps })}
        </SimpleFlexbox>
    );
};

_ColumnMiddle.propTypes = {
    componentList: PropTypes.array,
    componentProps: PropTypes.object
};

/**
 * Private component to render layout for right column in the results control
 * @param {Object} props - The components props
 * @param {Array} props.componentList - components to be rendered in this layout
 * @returns {_ColumnRight} Right column layout
 * @private
 */
const _ColumnRight = ({ componentList }) => {
    const { constructComponents } = pageContext.getProps().director;
    return (
        <SimpleFlexbox sm={4} display="flex" contentEnd="sm">
            {constructComponents(componentList)}
        </SimpleFlexbox>
    );
};

_ColumnRight.propTypes = {
    componentList: PropTypes.array
};

export { LayoutResultsControl };
// export for unit test
export { _Xs, _Sm, _Md, _ColumnLeft, _ColumnMiddle, _ColumnRight };
